// src/components/products/StockCell.tsx
import React, { useState, useRef } from 'react';
import { Package, ChevronDown } from 'lucide-react';

interface StockCellProps {
  product: any;
}

const StockCell: React.FC<StockCellProps> = ({ product }) => {
  const [hoveredProduct, setHoveredProduct] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('bottom');
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasVariants = product.variants && product.variants.length > 1;

  // Calculate total stock across all variants
  const totalStock = product.variants?.reduce((sum, variant) => {
    return sum + (variant.available_quantity || 0);
  }, 0) || 0;

  const handleMouseEnter = (e: React.MouseEvent, productId: number) => {
    // Clear any pending close timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Calculate position
    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setTooltipPosition(spaceBelow > 300 ? 'bottom' : 'top');
    setHoveredProduct(productId);
  };

  const handleMouseLeave = () => {
    // Small delay to allow moving mouse to tooltip
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredProduct(null);
    }, 100);
  };

  const handleTooltipMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setHoveredProduct(product.id);
  };

  // Single variant style
  if (!hasVariants) {
    return (
      <div className="flex items-center justify-center">
        <div className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-900">
          {totalStock} in stock
        </div>
      </div>
    );
  }

  // Multiple variants style
  return (
    <div
      className="relative flex items-center justify-center"
      onMouseLeave={handleMouseLeave}
    >
      <button
        onMouseEnter={(e) => handleMouseEnter(e, product.id)}
        className="inline-flex items-center px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm transition-colors"
      >
        <Package size={14} className="mr-1 text-gray-600" />
        <span className="font-medium">
          {product.variants?.length} variants
        </span>
        <span className="mx-1 text-gray-400">·</span>
        <span className="text-gray-900">{totalStock} total</span>
        <ChevronDown size={14} className="ml-1 text-gray-400" />
      </button>

      {hoveredProduct === product.id && (
        <div
          className={`absolute z-10 w-64 ${tooltipPosition === 'top'
            ? 'bottom-full mb-2'
            : 'mt-2'
          }`}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 py-2">
            <div className="max-h-60 overflow-y-auto">
              {product.variants?.map((variant) => {
                const variantStock = variant.available_quantity || 0;

                return (
                  <div
                    key={variant.id}
                    className="flex items-center px-3 py-2 hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {variant.name}
                      </p>
                    </div>

                    <div className="ml-2 flex items-center">
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                        variantStock > 10 ? 'bg-green-500' :
                        variantStock > 0 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`} />
                      <span className="text-sm font-mono">{variantStock}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t px-3 py-2 mt-1 bg-gray-50 rounded-b-lg">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">Total</span>
                <span className="font-mono font-medium">{totalStock}</span>
              </div>
            </div>
          </div>

          {/* Optional arrow */}
          {tooltipPosition === 'top' && (
            <div className="absolute left-4 bottom-0 transform translate-y-1/2 rotate-45 w-2 h-2 bg-white border-r border-b border-gray-200" />
          )}
        </div>
      )}
    </div>
  );
};

export default StockCell;